import { useCallback, useMemo, useState } from "react";
import type { PartnerActivityEntry } from "../model";
import type { ActivitySort, ActivitySortColumn } from "./activityColumns";
import {
  DEFAULT_ACTIVITY_FILTERS,
  DEFAULT_ACTIVITY_SORT,
  applyActivityFilters,
  averageResultCount,
  isDefaultActivityFilters,
  nextActivitySort,
  sortActivity,
} from "./activityView";
import type { ActivityFilters } from "./partnerDeepDive.types";

interface PartnerActivityState {
  partnerId: string;
  filters: ActivityFilters;
  sort: ActivitySort;
}

export interface PartnerActivityModel {
  entries: PartnerActivityEntry[];
  totalCount: number;
  averageResults: number | null;
  filters: {
    value: ActivityFilters;
    isDefault: boolean;
    set: <K extends keyof ActivityFilters>(key: K, value: ActivityFilters[K]) => void;
    reset: () => void;
  };
  sort: {
    value: ActivitySort;
    toggleColumn: (column: ActivitySortColumn) => void;
    set: (sort: ActivitySort) => void;
  };
}

function initialState(partnerId: string): PartnerActivityState {
  return {
    partnerId,
    filters: DEFAULT_ACTIVITY_FILTERS,
    sort: DEFAULT_ACTIVITY_SORT,
  };
}

/**
 * Stateful Recent Activity module for one Partner. It owns the filter/sort
 * transition rules and presents one projection to both responsive adapters.
 */
export function usePartnerActivity(input: {
  partnerId: string;
  entries: readonly PartnerActivityEntry[];
}): PartnerActivityModel {
  const { partnerId, entries } = input;
  const [storedState, setStoredState] = useState(() => initialState(partnerId));

  // A Partner change is a new Recent Activity context. Present its defaults
  // immediately rather than leaking the previous Partner's state for one render.
  const state = storedState.partnerId === partnerId ? storedState : initialState(partnerId);
  if (state !== storedState) setStoredState(state);

  const filteredEntries = useMemo(
    () => applyActivityFilters([...entries], state.filters),
    [entries, state.filters]
  );
  const shownEntries = useMemo(
    () => sortActivity(filteredEntries, state.sort),
    [filteredEntries, state.sort]
  );
  const averageResults = useMemo(() => averageResultCount(filteredEntries), [filteredEntries]);

  const updateCurrentPartner = useCallback(
    (update: (current: PartnerActivityState) => PartnerActivityState) => {
      setStoredState((current) =>
        update(current.partnerId === partnerId ? current : initialState(partnerId))
      );
    },
    [partnerId]
  );

  const setFilter = useCallback(
    <K extends keyof ActivityFilters>(key: K, value: ActivityFilters[K]) => {
      updateCurrentPartner((current) => ({
        ...current,
        filters: { ...current.filters, [key]: value },
      }));
    },
    [updateCurrentPartner]
  );

  const resetFilters = useCallback(
    () =>
      updateCurrentPartner((current) => ({
        ...current,
        filters: DEFAULT_ACTIVITY_FILTERS,
      })),
    [updateCurrentPartner]
  );

  const toggleSort = useCallback(
    (column: ActivitySortColumn) =>
      updateCurrentPartner((current) => ({
        ...current,
        sort: nextActivitySort(current.sort, column),
      })),
    [updateCurrentPartner]
  );

  const setSort = useCallback(
    (sort: ActivitySort) => updateCurrentPartner((current) => ({ ...current, sort })),
    [updateCurrentPartner]
  );

  return {
    entries: shownEntries,
    totalCount: entries.length,
    averageResults,
    filters: {
      value: state.filters,
      isDefault: isDefaultActivityFilters(state.filters),
      set: setFilter,
      reset: resetFilters,
    },
    sort: { value: state.sort, toggleColumn: toggleSort, set: setSort },
  };
}
